import { Box, Typography } from '@mui/material';
import { format, isToday, isYesterday } from 'date-fns';
import { FragmentType, graphql, useFragment } from '../../../__generated__';
import RequestItem from './RequestItem';

const RequestsList_QueryFragment = graphql(/* GraphQL */ `
  fragment RequestsList_QueryFragment on Query {
    agentTransactions(
      searchText: $searchText
      pageSize: $pageSize
      startPage: $startPage
    ) {
      dateCreated
      requestCount
      ...RequestItemTransaction
    }
  }
`);

export type RequestListProps = {
  query: FragmentType<typeof RequestsList_QueryFragment>;
  highlightRecipient?: boolean;
  loading?: boolean;
  setSearchText: (text: string) => void;
};

const getDateDisplay = (date: Date) => {
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  return format(date, 'MMMM d, yyyy');
};

const RequestList = (props: RequestListProps) => {
  const { loading, highlightRecipient } = props;
  const query = useFragment(RequestsList_QueryFragment, props.query);
  const dates: string[] = [];
  const requests = query.agentTransactions || [];
  const byDate = requests.reduce(
    (acc: { [key: string]: typeof req[] }, req) => {
      if (!req) return acc;
      const dateDisplay = getDateDisplay(new Date(parseInt(req.dateCreated)));
      if (!acc[dateDisplay]) {
        dates.push(dateDisplay);
        acc[dateDisplay] = [];
      }
      acc[dateDisplay].push(req);
      return acc;
    },
    {}
  );
  return (
    <Box sx={{ pl: 1, pt: 1, flexGrow: 1, pb: 15 }}>
      {(query.agentTransactions || []).length === 0 && (
        <Typography
          variant="subtitle2"
          sx={{ mt: 2, ml: 0, mb: 2, fontSize: 12, fontWeight: 'bold' }}
          key="emptyList"
          id="requestList"
        >
          No Requests
        </Typography>
      )}
      {dates.map((date) => (
        <Box key={date}>
          <Typography
            variant="subtitle2"
            sx={{ mt: 2, ml: 0, mb: 2, fontSize: 12, fontWeight: 'bold' }}
            key={`${date}`}
            id="date"
          >
            {date}
          </Typography>
          {byDate[date].map((req, index) => {
            return req ? (
              <RequestItem
                transaction={req}
                loading={loading}
                highlightRecipient={highlightRecipient}
                tint={byDate[date].length > 2 && index % 2 !== 0}
                key={index}
                setSearchText={props.setSearchText}
              />
            ) : null;
          })}
        </Box>
      ))}
    </Box>
  );
};

export default RequestList;
